import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { motion } from "framer-motion";
import { NavLink, useNavigate } from "react-router-dom";
import useTokenExpiryCheck from "../../utils/UseTokenExpiryCheck";
import { companyName, companyMoto } from "../../config";
import { backgroundImage, companyLogo } from "../../config";

const HomePage = () => {
  const navigate = useNavigate();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };
  const { isAuthenticated } = useSelector((state) => state.auth);
  useTokenExpiryCheck();

  const navLinkClass = ({ isActive }) =>
    `text-white text-sm font-medium hover:text-blue-200 transition-colors duration-300`;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="relative min-h-[calc(100vh-4rem)] overflow-hidden flex-grow"
      >
        {/* Background with parallax effect */}
        <motion.div
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="absolute inset-0 bg-center bg-cover bg-no-repeat"
          style={{
            backgroundImage: `url(${backgroundImage})`, // Use imported image
          }}
          //style={{ backgroundImage: `url(${imagePath})` }}
          //   style={{ backgroundImage: `url(${require("../../assets/ais.png")})` }}
        />

        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 h-full">
          {/* Buttons at top right */}
          <div className="flex justify-end pt-4">
            <motion.div className="flex gap-4" variants={itemVariants}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 bg-white/10 backdrop-blur-sm text-white rounded-lg font-semibold border-2 border-white/30 hover:bg-white/20 transition-colors duration-300"
                onClick={() => navigate("/new-applicant-list")}
              >
                Apply Now
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 bg-white/10 backdrop-blur-sm text-white rounded-lg font-semibold border-2 border-white/30 hover:bg-white/20 transition-colors duration-300"
                onClick={() => navigate("/login")}
              >
                Login Now
              </motion.button>
            </motion.div>
          </div>

          {/* Centered content */}
          <div className="flex flex-col justify-center h-full pt-10 pb-32">
            <motion.div
              variants={itemVariants}
              className="max-w-3xl mx-auto text-center"
            >
              <motion.h1
                className="text-4xl md:text-6xl font-bold text-white mb-6"
                variants={itemVariants}
              >
                Welcome to{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">
                  {companyName}
                </span>
              </motion.h1>

              <motion.p
                className="text-xl text-gray-200 mb-8"
                variants={itemVariants}
              >
                {companyMoto}
              </motion.p>
            </motion.div>

            {/* Stats Section (unchanged) */}
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16"
            >
              {[
                { number: "25+", label: "Years of Excellence" },
                { number: "2000+", label: "Students Enrolled" },
                { number: "100%", label: "Success Rate" },
                { number: "100+", label: "Expert Teachers" },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -5 }}
                  className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20"
                >
                  <motion.h3
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="text-3xl font-bold text-white mb-2"
                  >
                    {stat.number}
                  </motion.h3>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7 + index * 0.1 }}
                    className="text-gray-300"
                  >
                    {stat.label}
                  </motion.p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
        {/* Decorative Elements */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ delay: 1, duration: 1 }}
          className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-black/50 to-transparent"
        />
      </motion.div>
    </div>
  );
};

export default HomePage;
